// Finds money in contract text ("$4,500.00 per month", "Rs. 1,50,000/-", "EUR 12.000,00", "USD 2 million")
// and picks the amount most likely to be the contract's price, rent or fee.
import { isKnownCurrency, parseMoneyInput } from "./money";

export type Period = "hour" | "day" | "week" | "month" | "quarter" | "year";

export interface FoundAmount {
  /** Integer minor units (cents, paise). */
  minor: number;
  currency: string;
  /** Set when the amount recurs ("per month", "annual fee"). */
  period: Period | null;
  /** Set for a price per unit ("£780 per tonne", "$85 per user"): never the contract's total. */
  unit: string | null;
  /** The amount as written, e.g. "$4,500.00". */
  text: string;
  index: number;
  length: number;
  score: number;
}

const SYMBOLS: [string, string][] = [
  ["US$", "USD"], ["CA$", "CAD"], ["C$", "CAD"], ["AU$", "AUD"], ["A$", "AUD"], ["NZ$", "NZD"], ["S$", "SGD"],
  ["HK$", "HKD"], ["R$", "BRL"], ["₹", "INR"], ["Rs.", "INR"], ["Rs", "INR"], ["INR", "INR"], ["€", "EUR"], ["£", "GBP"],
  ["¥", "JPY"], ["₩", "KRW"], ["₱", "PHP"], ["₦", "NGN"], ["$", "USD"],
];

/** Codes accepted next to a number. Not every ISO code: "ALL 3 parties" isn't Albanian lek. */
const CODES = "USD|EUR|GBP|INR|CAD|AUD|NZD|SGD|HKD|AED|SAR|QAR|KWD|JPY|CNY|CHF|SEK|NOK|DKK|PLN|CZK|ZAR|BRL|MXN|MYR|THB|IDR|PHP|KRW|TRY|ILS";

const WORD_CURRENCIES: [RegExp, string][] = [
  [/^(?:us\s+|united\s+states\s+)?dollars?$/i, "USD"],
  [/^canadian\s+dollars?$/i, "CAD"],
  [/^australian\s+dollars?$/i, "AUD"],
  [/^singapore\s+dollars?$/i, "SGD"],
  [/^euros?$/i, "EUR"],
  [/^(?:pounds?\s+sterling|pounds?|gbp)$/i, "GBP"],
  [/^(?:indian\s+)?rupees?$/i, "INR"],
  [/^dirhams?$/i, "AED"],
  [/^yen$/i, "JPY"],
];

const MULTIPLIERS: Record<string, number> = { thousand: 1e3, lakh: 1e5, lakhs: 1e5, lac: 1e5, lacs: 1e5, million: 1e6, mn: 1e6, crore: 1e7, crores: 1e7, cr: 1e7, billion: 1e9, bn: 1e9 };

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const SYMBOL_RE = SYMBOLS.map(([s]) => esc(s)).join("|");
const NUM = "\\d{1,3}(?:[,.]\\d{2,3}(?!\\d))+(?:[.,]\\d{1,2}(?!\\d))?|\\d+(?:[.,]\\d{1,2}(?!\\d))?";
const MULT = `(?:\\s*(${Object.keys(MULTIPLIERS).join("|")})\\b)?`;
const WORDS = "(?:(?:us|united\\s+states|canadian|australian|singapore|indian)\\s+)?(?:dollars?|euros?|pounds?(?:\\s+sterling)?|rupees?|dirhams?|yen)";

// "$ 4,500.00", "Rs. 1,50,000/-", "USD 12,000", "€12.000,00"
const PREFIX_RE = new RegExp(`(?<![\\p{L}\\d])(${SYMBOL_RE}|${CODES})\\s?(${NUM})${MULT}(?:\\s*\\/-)?`, "giu");
// "12,000 USD", "5 lakh rupees", "1,000 dollars"
const SUFFIX_RE = new RegExp(`(?<![\\p{L}\\d.,$€£₹])(${NUM})${MULT}\\s*(${WORDS}|${CODES})\\b`, "giu");

const PERIOD_AFTER = /^\s*(?:\/-\s*)?(?:\([^)]{0,120}\)\s*)?(?:(?:us\s+)?dollars?\s*)?(?:(?:per|a|an|each|every|\/)\s*(?:calendar\s+)?(hour|hr|day|week|month|mo|quarter|year|yr|annum)\b|(hourly|daily|weekly|monthly|quarterly|yearly|annually|p\.?\s?m\.?|p\.?\s?a\.?)(?![\p{L}]))/iu;
// "monthly rent of", "monthly licence fee of", "monthly service fee of", "annual maintenance charges of"
const PERIOD_BEFORE = /\b(hourly|daily|weekly|monthly|quarterly|yearly|annual|annually)\s+(?:[\p{L}-]+\s+){0,2}?(?:rent(?:al)?|fees?|retainer|payments?|charges?|subscription|salary|rate|amount|compensation|remuneration|instal(?:l)?ments?)\b[^.;]{0,40}$/iu;

const PERIOD_WORDS: Record<string, Period> = {
  hour: "hour", hr: "hour", hourly: "hour", day: "day", daily: "day", week: "week", weekly: "week",
  month: "month", mo: "month", monthly: "month", pm: "month", quarter: "quarter", quarterly: "quarter",
  year: "year", yr: "year", annum: "year", yearly: "year", annual: "year", annually: "year", pa: "year",
};

const UNIT_AFTER = /^\s*(?:\/-\s*)?(?:\([^)]{0,60}\)\s*)?(?:per|\/|a|each|for\s+each)\s*(tonne|ton|mt|kg|kilogram|quintal|unit|piece|pc|pcs|nos?|item|user|seat|desk|cabin|workstation|licen[cs]e|sq\.?\s*ft|sqft|square\s+(?:feet|foot|metres?|meters?)|litre|liter|kwh|km|page|word|session|visit|class|person|head|room|night)\b/i;

const POSITIVE: [RegExp, number][] = [
  [/\bgrand\s+total\b|\btotal\s+(?:\w+\s+)?(?:contract\s+)?(?:value|price|amount|consideration|fees?|cost|sum|payable)\b|\baggregate\s+(?:value|price|amount|consideration|fees?)\b/i, 4],
  [/\bcontract\s+(?:value|price|sum|amount)\b|\bpurchase\s+price\b|\bconsideration\b/i, 4],
  [/\b(?:rent(?:al)?|licen[cs]e\s+fees?|subscription|retainer|compensation|remuneration|salary|professional\s+fees?)\b/i, 3],
  [/\b(?:fees?|price|charges?|payable|pay(?:ment)?s?|rate|cost|invoice[ds]?|billed|amount\s+of)\b/i, 2],
  [/\b(?:sum|amount)\b/i, 1],
];
const NEGATIVE: [RegExp, number][] = [
  // "an advance of Rs. 1,10,000" is a deposit; "payable in advance" is just when rent is due.
  [/\b(?:security\s+)?deposit\b|\bearnest\s+money\b|\b(?:token|booking)\s+(?:amount|money)\b|(?<!\bin\s)\badvance\s+(?:amount|payment|money|of)\b/i, -4],
  [/\bliab(?:le|ility)\b|\binsurance\b|\bcoverage\b|\bindemn\w*|\bdamages\b|\bpenalt\w*|\bfines?\b|\bbond\b|\bguarantee\b/i, -5],
  [/\blate\s+(?:fee|charge|payment)\b|\binterest\b|\bstamp\s+duty\b|\breimburs\w*|\bexpenses?\b|\bescalat\w*|\bdelay\b|\bprocessing\s+fee\b|\bcam\s+charges\b|\bmaintenance\s+charges\b|\bbrokerage\b/i, -3],
  [/\bbalance\b|\bper\s+day\s+of\b|\bpremium\b|\bsum\s+insured\b|\bidv\b|\bdeclared\s+value\b|\bdeductible\b|\bconsideration\s+price\s*\(rs\.?\)\s*:\s*0\b/i, -2],
  [/\bnot\s+(?:to\s+)?exceed\b|\bcap(?:ped)?\b|\bmaximum\b|\bup\s+to\b|\bthreshold\b/i, -1],
];

function mostCommon(values: string[]): string | null {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

function currencyFor(token: string, text: string): string | null {
  const t = token.trim();
  if (/^[A-Z]{3}$/.test(t) && isKnownCurrency(t) && !SYMBOLS.some(([s]) => s === t)) return t;
  if (t === "$") {
    // A bare "$" is US dollars unless the contract says otherwise.
    if (/\bcanadian\s+dollars?\b|\bCAD\b/i.test(text)) return "CAD";
    if (/\baustralian\s+dollars?\b|\bAUD\b/i.test(text)) return "AUD";
    if (/\bsingapore\s+dollars?\b|\bSGD\b/i.test(text)) return "SGD";
    if (/\bnew\s+zealand\s+dollars?\b|\bNZD\b/i.test(text)) return "NZD";
    return "USD";
  }
  const sym = SYMBOLS.find(([s]) => s === t);
  if (sym) return sym[1];
  for (const [re, code] of WORD_CURRENCIES) if (re.test(t.replace(/\s+/g, " "))) return code;
  const up = t.toUpperCase();
  return /^[A-Z]{3}$/.test(up) && isKnownCurrency(up) ? up : null;
}

function toMinor(num: string, currency: string, mult: string | undefined): number | null {
  const parsed = parseMoneyInput(num, currency);
  if (!parsed.ok || parsed.minor === null) return null;
  const m = mult ? (MULTIPLIERS[mult.toLowerCase()] ?? 1) : 1;
  const minor = parsed.minor * m;
  return Number.isSafeInteger(minor) && minor > 0 && minor <= 1e15 ? minor : null;
}

function periodOf(text: string, start: number, end: number): Period | null {
  const after = PERIOD_AFTER.exec(text.slice(end, end + 160));
  const word = after?.[1] ?? after?.[2] ?? PERIOD_BEFORE.exec(text.slice(Math.max(0, start - 60), start))?.[1];
  return word ? (PERIOD_WORDS[word.toLowerCase().replace(/[.\s]/g, "")] ?? null) : null;
}

/** Where the last match of `re` in `s` ends, or -1. */
function lastEnd(s: string, re: RegExp): number {
  let at = -1;
  for (const m of s.matchAll(new RegExp(re.source, `${re.flags.replace("g", "")}g`))) at = Math.max(at, m.index + m[0].length);
  return at;
}

// Dots that don't end a sentence ("i.e., Rs. 95,000"), and rent or fees used as a measure ("a deposit
// equal to two months' fees"): both blanked out (keeping positions) before an amount's context is read.
const ABBREV_DOTS = /\b(?:i\.e|e\.g|w\.e\.f|viz|no|nos|rs|mr|mrs|ms|dr|st|co|inc|ltd|pvt|approx)\./gi;
const MULTIPLE_OF = /\b(?:\w+|\(\d+\))\s+(?:\(\d+\)\s+)?months?['’]?s?\s+(?:rent(?:al)?|fees?|licen[cs]e\s+fees?|compensation)\b/gi;
const blank = (w: string) => " ".repeat(w.length);

function scoreOf(text: string, start: number, end: number): number {
  // Look back to the start of the sentence (or 140 characters), and ahead to the end of this amount's
  // own phrase: in "rent of Rs. 25,000 and a security deposit of …" the deposit is the next amount's.
  const back = text
    .slice(Math.max(0, start - 140), start)
    .replace(ABBREV_DOTS, (w) => w.replace(/\./g, " "))
    .replace(MULTIPLE_OF, blank);
  const sentence = back.slice(Math.max(0, back.search(/[^.;]*$/)));
  const ahead = text
    .slice(end, end + 100)
    .replace(/^\s*\/-/, "")
    .replace(/^\s*\([^)]{0,80}\)/, "")
    .split(/[.;,]|\b(?:and|plus|but|while|whereas)\b/i)[0]!;
  let score = 0;
  let positiveAt = -1;
  for (const [re, w] of POSITIVE) {
    const at = re.test(ahead) ? sentence.length + 1 : lastEnd(sentence, re);
    if (at < 0) continue;
    score = Math.max(score, w);
    positiveAt = Math.max(positiveAt, at);
  }
  // A word like "deposit" counts against the amount unless a price word sits between it and the amount.
  for (const [re, w] of NEGATIVE) {
    const at = lastEnd(sentence, re);
    if (re.test(ahead) || (at >= 0 && at >= positiveAt)) score += w;
  }
  return score;
}

/** Every amount with a recognisable currency, in document order. `text` should have collapsed whitespace. */
export function findAmounts(text: string): FoundAmount[] {
  const found: FoundAmount[] = [];
  const add = (index: number, length: number, num: string, mult: string | undefined, token: string) => {
    if (found.some((f) => index < f.index + f.length && f.index < index + length)) return;
    const currency = currencyFor(token, text);
    if (!currency) return;
    const minor = toMinor(num, currency, mult);
    if (minor === null) return;
    const end = index + length;
    const unit = UNIT_AFTER.exec(text.slice(end, end + 80))?.[1]?.toLowerCase() ?? null;
    found.push({ minor, currency, period: unit ? null : periodOf(text, index, end), unit, text: text.slice(index, end).trim(), index, length, score: unit ? -10 : scoreOf(text, index, end) });
  };
  for (const m of text.matchAll(PREFIX_RE)) add(m.index!, m[0].length, m[2]!, m[3], m[1]!);
  for (const m of text.matchAll(SUFFIX_RE)) add(m.index!, m[0].length, m[1]!, m[2], m[3]!);
  // Tables put the currency in the column heading ("Amount (Rs.)"), so a "Grand Total 2,95,000.00"
  // has no symbol of its own: it takes the document's currency.
  const docCurrency = mostCommon(found.map((f) => f.currency)) ?? (/\b(?:rs\.?|inr|rupees)\b|₹/i.test(text) ? "INR" : null);
  if (docCurrency) {
    for (const m of text.matchAll(/\b(grand\s+total|total\s+(?:amount|value|payable|cost|price|contract\s+value))\b[^\d\n.]{0,30}?(\d{1,3}(?:,\d{2,3})+(?:\.\d{1,2})?|\d{4,}(?:\.\d{1,2})?)(?![\d%])/gi)) {
      const at = m.index! + m[0].length - m[2]!.length;
      add(at, m[2]!.length, m[2]!, undefined, docCurrency);
      const f = found.find((x) => x.index === at);
      if (f) f.score = /grand/i.test(m[1]!) ? 6 : 5;
    }
  }
  return found.sort((a, b) => a.index - b.index);
}

/** The amount most likely to be what the contract costs, or null if nothing looks like a price. */
export function pickContractAmount(amounts: readonly FoundAmount[]): FoundAmount | null {
  let best: FoundAmount | null = null;
  for (const a of amounts) if (a.score >= 0 && (!best || a.score > best.score)) best = a;
  return best;
}

export const PERIOD_LABEL: Record<Period, string> = { hour: "hour", day: "day", week: "week", month: "month", quarter: "quarter", year: "year" };

/** How many times a recurring amount is paid over a term, or null when it can't be counted. */
export function periodsInTerm(period: Period, termMonths: number | null, termDays: number | null): number | null {
  if (period === "month" && termMonths) return termMonths;
  if (period === "quarter" && termMonths && termMonths % 3 === 0) return termMonths / 3;
  if (period === "year" && termMonths && termMonths % 12 === 0) return termMonths / 12;
  if (period === "week" && termDays && termDays % 7 === 0) return termDays / 7;
  return null;
}
